import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useDropzone } from "react-dropzone";
import ImagePickerImage from "../../../assets/images/gallery.png";
import ImagePlay from "../../../assets/images/play.png";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";

const StepTwo = forwardRef(
  ({ formData, setFormData, basicInfoOptions, categories, levelOptions, courseId }, ref) => {
    const [errors, setErrors] = useState({});
    const [subCategoryList, setSubCategoryList] = useState([]);
    const [images, setImages] = useState([]);
    const [videos, setVideos] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({});

    useImperativeHandle(ref, () => ({
      validate: () => {
        const newErrors = {};
        const courseInterface = formData.courseInterface || {};

        if (!courseInterface.basic_info_option_id)
          newErrors.basic_info_option_id = "Basic Info is required";
        if (!courseInterface.level) newErrors.level = "Level is required";
        if (!courseInterface.category) newErrors.category = "Category is required";
        if (!courseInterface.subcategory) newErrors.subcategory = "Subcategory is required";
        if (!courseInterface.taught?.trim()) newErrors.taught = "This field is required";
        if (!images || images.length === 0) newErrors.images = "At least 1 image is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
      },
    }));

    useEffect(() => {
      if (categories.length > 0 && formData?.courseInterface?.category) {
        const subCategoryFilterResult = categories.find(
          (item) => item.id === Number(formData.courseInterface.category)
        );
        setSubCategoryList(subCategoryFilterResult?.Subcategories || []);
      } else {
        setSubCategoryList([]);
      }
    }, [formData?.courseInterface?.category, categories]);

    useEffect(() => {
      if (courseId && courseId > 0) {
        const savedImages = localStorage.getItem("uploadedImages");
        if (savedImages) {
          try {
            const parsedImages = JSON.parse(savedImages);
            setImages(parsedImages);
            setFormData((prev) => ({
              ...prev,
              courseInterface: {
                ...prev.courseInterface,
                images: parsedImages,
              },
            }));
          } catch (err) {
            console.error("Error parsing images from localStorage:", err);
          }
        } else if (formData?.courseInterface?.images && formData?.courseInterface?.images !== null) {
          try {
            const imgArr = JSON.parse(formData.courseInterface.images);
            const formattedImages = imgArr.map((img, index) => {
              if (typeof img === "string") {
                return {
                  id: `existing-${index}`,
                  name: `Image ${index + 1}`,
                  type: "image/jpeg",
                  size: 0,
                  previewUrl: process.env.REACT_APP_GALLERY_URL + img,
                  isUploaded: true,
                  isExisting: true,
                  url: img,
                };
              }
              return img;
            });
            setImages(formattedImages);
            localStorage.setItem("uploadedImages", JSON.stringify(formattedImages));
            setFormData((prev) => ({
              ...prev,
              courseInterface: {
                ...prev.courseInterface,
                images: formattedImages,
              },
            }));
          } catch (err) {
            console.error("Error parsing images from formData:", err);
          }
        }

        // Load videos from localStorage or formData
        const savedVideos = localStorage.getItem("uploadedVideos");
        if (savedVideos) {
          try {
            const parsedVideos = JSON.parse(savedVideos);
            setVideos(parsedVideos);
            setFormData((prev) => ({
              ...prev,
              courseInterface: {
                ...prev.courseInterface,
                videos: parsedVideos,
              },
            }));
          } catch (err) {
            console.error("Error parsing videos from localStorage:", err);
            toast.error("Error loading videos from storage");
          }
        } else if (formData?.courseInterface?.videos && formData?.courseInterface?.videos !== null) {
          try {
            const videoArr = JSON.parse(formData.courseInterface.videos);
            const formattedVideos = videoArr.map((vid, index) => {
              if (typeof vid === "string") {
                return {
                  id: `existing-${index}`,
                  name: `Video ${index + 1}`,
                  type: "video/mp4",
                  size: 0,
                  previewUrl: process.env.REACT_APP_GALLERY_URL + vid,
                  isUploaded: true,
                  isExisting: true,
                  url: vid, // Store the original URL for submission
                };
              }
              return vid;
            });
            setVideos(formattedVideos);
            localStorage.setItem("uploadedVideos", JSON.stringify(formattedVideos));
            setFormData((prev) => ({
              ...prev,
              courseInterface: {
                ...prev.courseInterface,
                videos: formattedVideos,
              },
            }));
          } catch (err) {
            console.error("Error parsing videos from formData:", err);
          }
        }
      } else {
        // Clear for new course
        setImages([]);
        setVideos([]);
        localStorage.removeItem("uploadedImages");
        localStorage.removeItem("uploadedVideos");
        setFormData((prev) => ({
          ...prev,
          courseInterface: {
            ...prev.courseInterface,
            images: [],
            videos: [],
          },
        }));
      }
    }, [courseId]);

    const handleChange = (field) => (e) => {
      const value = e.target.value;
      setFormData((prev) => ({
        ...prev,
        courseInterface: {
          ...prev.courseInterface,
          [field]: value,
        },
      }));
      setErrors((prev) => ({ ...prev, [field]: "" }));
    };

    const onDropImages = (acceptedFiles) => {
      const maxSize = 5 * 1024 * 1024; // 5MB
      const newImages = acceptedFiles
        .filter((file) => file.type.startsWith("image/"))
        .map((file) => {
          if (file.size > maxSize) {
            toast.error(`${file.name} exceeds 5MB`);
            return null;
          }
          return {
            id: uuidv4(),
            name: file.name,
            type: file.type,
            size: file.size,
            previewUrl: URL.createObjectURL(file),
            file,
            isUploaded: false,
          };
        })
        .filter(Boolean);

      if (newImages.length < acceptedFiles.length) {
        toast.error("Some selected files are not valid images");
      }

      setImages((prev) => {
        const updatedImages = [...prev, ...newImages];
        localStorage.setItem("uploadedImages", JSON.stringify(updatedImages));
        setFormData((prev) => ({
          ...prev,
          courseInterface: {
            ...prev.courseInterface,
            images: updatedImages,
          },
        }));
        return updatedImages;
      });
    };

    const onDropVideos = (acceptedFiles) => {
      const newVideos = acceptedFiles
        .filter((file) => file.type.startsWith("video/"))
        .map((file) => ({
          id: uuidv4(),
          file,
          name: file.name,
          type: file.type,
          size: file.size,
          previewUrl: URL.createObjectURL(file),
          isUploaded: false,
        }));

      if (newVideos.length < acceptedFiles.length) {
        toast.error("Some selected files are not valid videos");
      }

      setVideos((prev) => {
        const updatedVideos = [...prev, ...newVideos];
        localStorage.setItem("uploadedVideos", JSON.stringify(updatedVideos));
        setFormData((prev) => ({
          ...prev,
          courseInterface: {
            ...prev.courseInterface,
            videos: updatedVideos,
          },
        }));
        return updatedVideos;
      });
    };

    const removeImage = (index) => {
      if (images.length > 0) {
        const updatedImages = images.filter((_, i) => i !== index);
        setImages(updatedImages);
        localStorage.setItem("uploadedImages", JSON.stringify(updatedImages));
        setFormData((prev) => ({
          ...prev,
          courseInterface: {
            ...prev.courseInterface,
            images: updatedImages,
          },
        }));
      }
    };

    const removeVideo = (index) => {
      if (videos.length > 0) {
        const updatedVideos = videos.filter((_, i) => i !== index);
        setVideos(updatedVideos);
        localStorage.setItem("uploadedVideos", JSON.stringify(updatedVideos));
        setFormData((prev) => ({
          ...prev,
          courseInterface: {
            ...prev.courseInterface,
            videos: updatedVideos,
          },
        }));
      }
    };

    const { getRootProps: getImageRootProps, getInputProps: getImageInputProps, isDragActive: isImageDragActive } =
      useDropzone({
        onDrop: onDropImages,
        accept: {
          "image/jpeg": [".jpg", ".jpeg"],
          "image/png": [".png"],
        },
        multiple: true,
      });

    const { getRootProps: getVideoRootProps, getInputProps: getVideoInputProps, isDragActive: isVideoDragActive } =
      useDropzone({
        onDrop: onDropVideos,
        accept: {
          "video/mp4": [".mp4"],
          "video/quicktime": [".mov"],
          "video/x-msvideo": [".avi"],
        },
        multiple: true,
      });

    return (
      <div className="card-default-style step-form-holder">
        <div className="step-form-holder-inner mx-auto my-10 my-lg-17">
          <div className="mb-10 mb-sm-20 mb-md-32">
            <h2 className="ah-heading-04 fw-bold text-black mb-10">Course Interface</h2>
            <p className="ah-text-sm fw-semibold mb-0">
              Your course interface is crucial to your success on EGWB. When done right, it can also boost your visibility
              in search engines like Google. As you complete this section, focus on creating a compelling Course Landing
              Page that clearly demonstrates the value of your course and why someone would want to enroll.
            </p>
          </div>
          <div className="card-default-style py-lg-36 px-lg-30 py-xl-42 px-xl-36">
            <div className="d-flex justify-content-between flex-wrap gap-3">
              <div className="form-group mb-16 mb-md-24 flex-grow-1 flex-shrink-0">
                <label htmlFor="basic_info_option_id" className="form-label fw-semibold mb-8 mb-lg-12">
                  Basic info
                </label>
                <select
                  id="basic_info_option_id"
                  className="form-select form-control"
                  value={formData.courseInterface?.basic_info_option_id || ""}
                  onChange={handleChange("basic_info_option_id")}
                >
                  <option value="">Select Language</option>
                  {basicInfoOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.text}
                    </option>
                  ))}
                </select>
                {errors.basic_info_option_id && (
                  <div className="text-danger">{errors.basic_info_option_id}</div>
                )}
              </div>

              <div className="form-group mb-16 mb-md-24 flex-grow-1 flex-shrink-0">
                <label htmlFor="selectLevel" className="form-label fw-semibold mb-8 mb-lg-12">
                  Select Level
                </label>
                <select
                  id="selectLevel"
                  className="form-select form-control"
                  value={formData.courseInterface?.level || ""}
                  onChange={handleChange("level")}
                >
                  <option value="">-Select Level-</option>
                  {levelOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.level}
                    </option>
                  ))}
                </select>
                {errors.level && <div className="text-danger">{errors.level}</div>}
              </div>

              <div className="form-group mb-16 mb-md-24 flex-grow-1 flex-shrink-0">
                <label htmlFor="category" className="form-label fw-semibold mb-8 mb-lg-12">
                  Category
                </label>
                <select
                  id="category"
                  className="form-select form-control"
                  value={formData.courseInterface?.category || ""}
                  onChange={handleChange("category")}
                >
                  <option value="">Select Category</option>
                  {categories.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                {errors.category && <div className="text-danger">{errors.category}</div>}
              </div>

              <div className="form-group mb-16 mb-md-24 flex-grow-1 flex-shrink-0">
                <label htmlFor="subCategory" className="form-label fw-semibold mb-8 mb-lg-12">
                  Select Sub Category
                </label>
                <select
                  id="subCategory"
                  className="form-select form-control"
                  value={formData.courseInterface?.subcategory || ""}
                  onChange={handleChange("subcategory")}
                >
                  <option value="">-Select Sub-Category-</option>
                  {subCategoryList?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                {errors.subcategory && <div className="text-danger">{errors.subcategory}</div>}
              </div>
            </div>

            <div className="form-group mb-16 mb-md-24">
              <label htmlFor="taught" className="form-label fw-semibold mb-8 mb-lg-12">
                What is primarily taught in your course?
              </label>
              <input
                type="text"
                className="form-control"
                id="taught"
                placeholder="e.g landscape Photography"
                value={formData.courseInterface?.taught || ""}
                onChange={handleChange("taught")}
              />
              {errors.taught && <div className="text-danger">{errors.taught}</div>}
            </div>

            <div className="media-uploader d-flex flex-column">
              <div>
                <label htmlFor="" className="form-label fw-semibold mb-8 mb-lg-12">
                  Course Image
                </label>
                <div className="upload-section mb-10">
                  <div
                    {...getImageRootProps()}
                    className={`upload-box d-flex justify-content-center align-content-center text-center ${
                      isImageDragActive ? "drag-active" : ""
                    }`}
                    style={{ border: "2px dashed rgba(118, 156, 25, 0.32)" }}
                  >
                    <input {...getImageInputProps()} />
                    <div className="upload-content d-flex flex-column fw-bold theme-color gap-2">
                      <div className="ah-text-sm">
                        {isImageDragActive ? "Drop the images here ..." : "Drag & drop images or click to select"}
                      </div>
                      <div className="text-center">
                        <img src={ImagePickerImage} className="img-fluid mx-auto" alt="Upload Icon" />
                      </div>
                      <div className="text-black fw-normal ah-text-sm">JPG, PNG, JPEG (Max 5MB)</div>
                    </div>
                  </div>
                  {images?.length > 0 && (
                    <div className="preview mt-16 pt-16 d-flex flex-wrap border-top">
                      {images?.map((image, index) => (
                        <div className="preview-item" key={image.id}>
                          <img
                            src={image.previewUrl}
                            alt={image.name}
                            style={{ maxWidth: "100px", maxHeight: "100px" }}
                          />
                          <button className="remove-btn" onClick={() => removeImage(index)}>
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {errors.images && <div className="text-danger">{errors.images}</div>}
                </div>
                <p className="ah-text-sm gray-color-300 mb-0">
                  Upload your course image here. It must meet our course image quality standards to be accepted. Important
                  guidelines: 750x422 pixels, .jpg, .jpeg, or .png, no text on the image
                </p>
              </div>

              <div>
                <label htmlFor="" className="form-label fw-semibold mb-8 mb-lg-12">
                  Promotional Video
                </label>
                <div className="upload-section mb-10">
                  <div
                    {...getVideoRootProps()}
                    className={`upload-box d-flex justify-content-center align-content-center text-center ${
                      isVideoDragActive ? "drag-active" : ""
                    }`}
                    style={{ border: "2px dashed rgba(118, 156, 25, 0.32)" }}
                  >
                    <input {...getVideoInputProps()} />
                    <div className="upload-content d-flex flex-column fw-bold theme-color">
                      <div className="ah-text-sm">
                        {isVideoDragActive ? "Drop the videos here ..." : "Drag & drop videos or click to select"}
                      </div>
                      <div className="video-icon mx-auto my-5 d-flex justify-content-center align-items-center gap-2">
                        <span>
                          <img src={ImagePlay} alt="Video Upload Icon" className="img-fluid mx-auto" />
                        </span>
                      </div>
                      <div className="text-black fw-normal ah-text-sm">MP4, MOV, AVI</div>
                    </div>
                  </div>
                  {videos.length > 0 && (
                    <div className="preview mt-16 pt-16 d-flex flex-wrap border-top">
                      {videos.map((vid, index) => (
                        <div className="preview-item mb-3" key={vid.id}>
                          <video controls style={{ maxWidth: "100%" }}>
                            <source src={vid.previewUrl} type={vid.type} />
                          </video>
                          {uploadProgress[vid.id] > 0 && uploadProgress[vid.id] < 100 && (
                            <div className="progress">
                              <div
                                className="progress-bar"
                                role="progressbar"
                                style={{ width: `${uploadProgress[vid.id]}%` }}
                                aria-valuenow={uploadProgress[vid.id]}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              >
                                {uploadProgress[vid.id]}%
                              </div>
                            </div>
                          )}
                          {!vid.isUploaded && (
                            <button className="remove-btn" onClick={() => removeVideo(index)}>
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {errors.videos && <div className="text-danger">{errors.videos}</div>}
                </div>
                <p className="ah-text-sm gray-color-300 mb-0">
                  Your promo video is a quick and compelling way for students to preview what they’ll learn in your course.
                  Students considering your course are more likely to enroll if your promo video is well-made
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default StepTwo;

// import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
// import { useDropzone } from "react-dropzone";
// import ImagePickerImage from "../../../assets/images/gallery.png";
// import ImagePlay from "../../../assets/images/play.png";
// import toast from "react-hot-toast";
// import { v4 as uuidv4 } from "uuid";

// const StepTwo = forwardRef(
//   ({ formData, setFormData, basicInfoOptions, categories, levelOptions, courseId }, ref) => {
//     const [errors, setErrors] = useState({});
//     const [subCategoryList, setSubCategoryList] = useState([]);
//     const [images, setImages] = useState([]);
//     const [videos, setVideos] = useState([]);
//     const [uploadProgress, setUploadProgress] = useState({});

//     useImperativeHandle(ref, () => ({
//       validate: () => {
//         const newErrors = {};
//         const courseInterface = formData.courseInterface || {};

     
//         if (!courseInterface.basic_info_option_id)
//           newErrors.basic_info_option_id = "Basic Info is required";
//         if (!courseInterface.level) newErrors.level = "Level is required";
//         if (!courseInterface.category) newErrors.category = "Category is required";
//         if (!courseInterface.subcategory) newErrors.subcategory = "Subcategory is required";
//         if (!courseInterface.taught?.trim()) newErrors.taught = "This field is required";
//         if (!images || images.length === 0) newErrors.images = "At least 1 image is required";
//         // if (!videos || videos.length === 0) newErrors.videos = "At least 1 video is required"; 

//         setErrors(newErrors);
//         return Object.keys(newErrors).length === 0;
//       },
//     }));

//     useEffect(() => {
//       if (categories.length > 0 && formData?.courseInterface?.category) {
//         const subCategoryFilterResult = categories.find(
//           (item) => item.id === Number(formData.courseInterface.category)
//         );
//         setSubCategoryList(subCategoryFilterResult?.Subcategories || []);
//       } else {
//         setSubCategoryList([]);
//       }
//     }, [formData?.courseInterface?.category, categories]);

//     useEffect(() => {
//       // Only load from localStorage when editing (courseId > 0)
//       if (courseId && courseId > 0) {
//         // Load images from localStorage for edit mode
//         const savedImages = localStorage.getItem("uploadedImages");
//         if (savedImages) {
//           try {
//             const parsedImages = JSON.parse(savedImages);
//             setImages(parsedImages);
//           } catch (err) {
//             console.error("Error parsing images from localStorage:", err);
//           }
//         } else if (formData?.courseInterface?.images && formData?.courseInterface?.images !== null) {
//         try {
//           const imgArr = JSON.parse(formData.courseInterface.images);
//           // Convert URL strings to image objects for display
//           const formattedImages = imgArr.map((img, index) => {
//             if (typeof img === 'string') {
//               // This is a URL from database
//               return {
//                 id: `existing-${index}`,
//                 name: `Image ${index + 1}`,
//                 type: 'image/jpeg', // Default type
//                 size: 0,
//                 previewUrl: process.env.REACT_APP_GALLERY_URL + img,
//                 isUploaded: true,
//                 isExisting: true
//               };
//             }
//             return img; // Already an object
//           });
//           setImages(formattedImages);
//           localStorage.setItem("uploadedImages", JSON.stringify(formattedImages));
//         } catch (err) {
//           console.error("Error parsing images from formData:", err);
//         }
//       }

//       // Load videos from localStorage
//       const savedVideos = localStorage.getItem("uploadedVideos");
//       if (savedVideos) {
//         try {
//           const parsedVideos = JSON.parse(savedVideos);
//           setVideos(parsedVideos);
//           setFormData((prev) => ({
//             ...prev,
//             courseInterface: {
//               ...prev.courseInterface,
//               videos: parsedVideos,
//             },
//           }));
//         } catch (err) {
//           console.error("Error parsing videos from localStorage:", err);
//           toast.error("Error loading videos from storage");
//         }
//       } else if (formData?.courseInterface?.videos && formData?.courseInterface?.videos !== null) {
//         try {
//           const videoArr = JSON.parse(formData.courseInterface.videos);
//           // Convert URL strings to video objects for display
//           const formattedVideos = videoArr.map((vid, index) => {
//             if (typeof vid === 'string') {
//               // This is a URL from database
//               return {
//                 id: `existing-${index}`,
//                 name: `Video ${index + 1}`,
//                 type: 'video/mp4', // Default type
//                 size: 0,
//                 previewUrl: process.env.REACT_APP_GALLERY_URL + vid,
//                 isUploaded: true,
//                 isExisting: true
//               };
//             }
//             return vid; // Already an object
//           });
//           setVideos(formattedVideos);
//           localStorage.setItem("uploadedVideos", JSON.stringify(formattedVideos));
//         } catch (err) {
//           console.error("Error parsing videos from formData:", err);
//         }
//       }
//       } else {
//         // For new course creation, clear any existing images/videos
//         setImages([]);
//         setVideos([]);
//         localStorage.removeItem("uploadedImages");
//         localStorage.removeItem("uploadedVideos");
//       }
//     }, [courseId]);

//     const handleChange = (field) => (e) => {
//       const value = e.target.value;
//       setFormData((prev) => ({
//         ...prev,
//         courseInterface: {
//           ...prev.courseInterface,
//           [field]: value,
//         },
//       }));
//       setErrors((prev) => ({ ...prev, [field]: "" }));
//     };

//     const onDropImages = (acceptedFiles) => {
//       const maxSize = 5 * 1024 * 1024; // 5MB
//       const newImages = acceptedFiles
//         .filter((file) => file.type.startsWith("image/"))
//         .map((file) => {
//           if (file.size > maxSize) {
//             toast.error(`${file.name} exceeds 5MB`);
//             return null;
//           }
//           return {
//             id: uuidv4(),
//             name: file.name,
//             type: file.type,
//             size: file.size,
//             previewUrl: URL.createObjectURL(file),
//             file,
//             isUploaded: false,
//           };
//         })
//         .filter(Boolean);

//       if (newImages.length < acceptedFiles.length) {
//         toast.error("Some selected files are not valid images");
//       }

//       setImages((prev) => {
//         const updatedImages = [...prev, ...newImages];
//         localStorage.setItem("uploadedImages", JSON.stringify(updatedImages));
//         return updatedImages;
//       });

//       setFormData((prev) => ({
//         ...prev,
//         courseInterface: {
//           ...prev.courseInterface,
//           images: [...(prev.courseInterface.images || []), ...newImages],
//         },
//       }));
//     };

//     const onDropVideos = (acceptedFiles) => {
//       const newVideos = acceptedFiles
//         .filter((file) => file.type.startsWith("video/"))
//         .map((file) => ({
//           id: uuidv4(),
//           file,
//           name: file.name,
//           type: file.type,
//           size: file.size,
//           previewUrl: URL.createObjectURL(file),
//           isUploaded: false,
//         }));

//       if (newVideos.length < acceptedFiles.length) {
//         toast.error("Some selected files are not valid videos");
//       }

//       setVideos((prev) => {
//         const updatedVideos = [...prev, ...newVideos];
//         localStorage.setItem("uploadedVideos", JSON.stringify(updatedVideos));
//         return updatedVideos;
//       });

//       setFormData((prev) => ({
//         ...prev,
//         courseInterface: {
//           ...prev.courseInterface,
//           videos: [...(prev.courseInterface.videos || []), ...newVideos],
//         },
//       }));
//     };

//     const removeImage = (id) => {
//       if(images.length > 0){
//         const updatedImages = images.filter((item, index) => index !== id);
//         setImages(updatedImages);
//         localStorage.setItem("uploadedImages", JSON.stringify(updatedImages));
//       }
//     };

//     const removeVideo = (id) => {
//       if(videos.length > 0){
//         const updatedVideos = videos.filter((item, index) => index !== id);
//         setVideos(updatedVideos);
//         localStorage.setItem("uploadedVideos", JSON.stringify(updatedVideos));
//       }
//     };

//     const { getRootProps: getImageRootProps, getInputProps: getImageInputProps, isDragActive: isImageDragActive } =
//       useDropzone({
//         onDrop: onDropImages,
//         accept: {
//           "image/jpeg": [".jpg", ".jpeg"],
//           "image/png": [".png"],
//         },
//         multiple: true,
//       });

//     const { getRootProps: getVideoRootProps, getInputProps: getVideoInputProps, isDragActive: isVideoDragActive } =
//       useDropzone({
//         onDrop: onDropVideos,
//         accept: {
//           "video/mp4": [".mp4"],
//           "video/quicktime": [".mov"],
//           "video/x-msvideo": [".avi"],
//         },
//         multiple: true,
//       });

//     useEffect(() => {
//       if (images.length > 0) {
//         localStorage.setItem("uploadedImages", JSON.stringify(images));
//         setFormData((prev) => ({
//           ...prev,
//           courseInterface: {
//             ...prev.courseInterface,
//             images,
//           },
//         }));
//       } else {
//         localStorage.removeItem("uploadedImages");
//         setFormData((prev) => ({
//           ...prev,
//           courseInterface: {
//             ...prev.courseInterface,
//             images: [],
//           },
//         }));
//       }
//     }, [images]);

//     useEffect(() => {
//       if (videos.length > 0) {
//         localStorage.setItem("uploadedVideos", JSON.stringify(videos));
//         setFormData((prev) => ({
//           ...prev,
//           courseInterface: {
//             ...prev.courseInterface,
//             videos,
//           },
//         }));
//       } else {
//         localStorage.removeItem("uploadedVideos");
//         setFormData((prev) => ({
//           ...prev,
//           courseInterface: {
//             ...prev.courseInterface,
//             videos: [],
//           },
//         }));
//       }
//     }, [videos]);

//     return (
//       <div className="card-default-style step-form-holder">
//         <div className="step-form-holder-inner mx-auto my-10 my-lg-17">
//           <div className="mb-10 mb-sm-20 mb-md-32">
//             <h2 className="ah-heading-04 fw-bold text-black mb-10">Course Interface</h2>
//             <p className="ah-text-sm fw-semibold mb-0">
//               Your course interface is crucial to your success on EGWB. When done right, it can also boost your visibility
//               in search engines like Google. As you complete this section, focus on creating a compelling Course Landing
//               Page that clearly demonstrates the value of your course and why someone would want to enroll.
//             </p>
//           </div>
//           <div className="card-default-style py-lg-36 px-lg-30 py-xl-42 px-xl-36">
      

//             <div className="d-flex justify-content-between flex-wrap gap-3">
//               <div className="form-group mb-16 mb-md-24 flex-grow-1 flex-shrink-0">
//                 <label htmlFor="basic_info_option_id" className="form-label fw-semibold mb-8 mb-lg-12">
//                   Basic info
//                 </label>
//                 <select
//                   id="basic_info_option_id"
//                   className="form-select form-control"
//                   value={formData.courseInterface?.basic_info_option_id || ""}
//                   onChange={handleChange("basic_info_option_id")}
//                 >
//                   <option value="">Select Language</option>
//                   {basicInfoOptions.map((item) => (
//                     <option key={item.id} value={item.id}>
//                       {item.text}
//                     </option>
//                   ))}
//                 </select>
//                 {errors.basic_info_option_id && (
//                   <div className="text-danger">{errors.basic_info_option_id}</div>
//                 )}
//               </div>

//               <div className="form-group mb-16 mb-md-24 flex-grow-1 flex-shrink-0">
//                 <label htmlFor="selectLevel" className="form-label fw-semibold mb-8 mb-lg-12">
//                   Select Level
//                 </label>
//                 <select
//                   id="selectLevel"
//                   className="form-select form-control"
//                   value={formData.courseInterface?.level || ""}
//                   onChange={handleChange("level")}
//                 >
//                   <option value="">-Select Level-</option>
//                   {levelOptions.map((item) => (
//                     <option key={item.id} value={item.id}>
//                       {item.level}
//                     </option>
//                   ))}
//                 </select>
//                 {errors.level && <div className="text-danger">{errors.level}</div>}
//               </div>

//               <div className="form-group mb-16 mb-md-24 flex-grow-1 flex-shrink-0">
//                 <label htmlFor="category" className="form-label fw-semibold mb-8 mb-lg-12">
//                   Category
//                 </label>
//                 <select
//                   id="category"
//                   className="form-select form-control"
//                   value={formData.courseInterface?.category || ""}
//                   onChange={handleChange("category")}
//                 >
//                   <option value="">Select Category</option>
//                   {categories.map((item) => (
//                     <option key={item.id} value={item.id}>
//                       {item.name}
//                     </option>
//                   ))}
//                 </select>
//                 {errors.category && <div className="text-danger">{errors.category}</div>}
//               </div>

//               <div className="form-group mb-16 mb-md-24 flex-grow-1 flex-shrink-0">
//                 <label htmlFor="subCategory" className="form-label fw-semibold mb-8 mb-lg-12">
//                   Select Sub Category
//                 </label>
//                 <select
//                   id="subCategory"
//                   className="form-select form-control"
//                   value={formData.courseInterface?.subcategory || ""}
//                   onChange={handleChange("subcategory")}
//                 >
//                   <option value="">-Select Sub-Category-</option>
//                   {subCategoryList?.map((item) => (
//                     <option key={item.id} value={item.id}>
//                       {item.name}
//                     </option>
//                   ))}
//                 </select>
//                 {errors.subcategory && <div className="text-danger">{errors.subcategory}</div>}
//               </div>
//             </div>

//             <div className="form-group mb-16 mb-md-24">
//               <label htmlFor="taught" className="form-label fw-semibold mb-8 mb-lg-12">
//                 What is primarily taught in your course?
//               </label>
//               <input
//                 type="text"
//                 className="form-control"
//                 id="taught"
//                 placeholder="e.g landscape Photography"
//                 value={formData.courseInterface?.taught || ""}
//                 onChange={handleChange("taught")}
//               />
//               {errors.taught && <div className="text-danger">{errors.taught}</div>}
//             </div>

//             <div className="media-uploader d-flex flex-column">
//               <div>
//                 <label htmlFor="" className="form-label fw-semibold mb-8 mb-lg-12">
//                   Course Image
//                 </label>
//                 <div className="upload-section mb-10">
//                   <div
//                     {...getImageRootProps()}
//                     className={`upload-box d-flex justify-content-center align-content-center text-center ${
//                       isImageDragActive ? "drag-active" : ""
//                     }`}
//                     style={{ border: "2px dashed rgba(118, 156, 25, 0.32)" }}
//                   >
//                     <input {...getImageInputProps()} />
//                     <div className="upload-content d-flex flex-column fw-bold theme-color gap-2">
//                       <div className="ah-text-sm">
//                         {isImageDragActive ? "Drop the images here ..." : "Drag & drop images or click to select"}
//                       </div>
//                       <div className="text-center">
//                         <img src={ImagePickerImage} className="img-fluid mx-auto" alt="Upload Icon" />
//                       </div>
//                       <div className="text-black fw-normal ah-text-sm">JPG, PNG, JPEG (Max 5MB)</div>
//                     </div>
//                   </div>
//                   {images?.length > 0 && (
//                     <div className="preview mt-16 pt-16 d-flex flex-wrap border-top">
//                       {images?.map((image, index) => (
//                         <div className="preview-item" key={index}>
//                           <img
//                             src={image.previewUrl ? image.previewUrl : process.env.REACT_APP_GALLERY_URL + image}
//                             alt={image.name}
//                             style={{ maxWidth: "100px", maxHeight: "100px" }}
//                           />
//                           <button className="remove-btn" onClick={() => removeImage(index)}>
//                             ×
//                           </button>
//                         </div>
//                       ))}
//                     </div>
//                   )}
//                   {errors.images && <div className="text-danger">{errors.images}</div>}
//                 </div>
//                 <p className="ah-text-sm gray-color-300 mb-0">
//                   Upload your course image here. It must meet our course image quality standards to be accepted. Important
//                   guidelines: 750x422 pixels, .jpg, .jpeg, or .png, no text on the image
//                 </p>
//               </div>

//               <div>
//                 <label htmlFor="" className="form-label fw-semibold mb-8 mb-lg-12">
//                   Promotional Video
//                 </label>
//                 <div className="upload-section mb-10">
//                   <div
//                     {...getVideoRootProps()}
//                     className={`upload-box d-flex justify-content-center align-content-center text-center ${
//                       isVideoDragActive ? "drag-active" : ""
//                     }`}
//                     style={{ border: "2px dashed rgba(118, 156, 25, 0.32)" }}
//                   >
//                     <input {...getVideoInputProps()} />
//                     <div className="upload-content d-flex flex-column fw-bold theme-color">
//                       <div className="ah-text-sm">
//                         {isVideoDragActive ? "Drop the videos here ..." : "Drag & drop videos or click to select"}
//                       </div>
//                       <div className="video-icon mx-auto my-5 d-flex justify-content-center align-items-center gap-2">
//                         <span>
//                           <img src={ImagePlay} alt="Video Upload Icon" className="img-fluid mx-auto" />
//                         </span>
//                       </div>
//                       <div className="text-black fw-normal ah-text-sm">MP4, MOV, AVI</div>
//                     </div>
//                   </div>
//                   {videos.length > 0 && (
//                     <div className="preview mt-16 pt-16 d-flex flex-wrap border-top">
//                       {videos.map((vid, index) => (
//                         <div className="preview-item mb-3" key={index}>
//                           <video controls style={{ maxWidth: "100%" }}>
//                             <source
//                               src={vid.previewUrl ? vid.previewUrl : process.env.REACT_APP_GALLERY_URL + vid}
//                               type={vid.type}
//                             />
//                           </video>
//                           {uploadProgress[vid.id] > 0 && uploadProgress[vid.id] < 100 && (
//                             <div className="progress">
//                               <div
//                                 className="progress-bar"
//                                 role="progressbar"
//                                 style={{ width: `${uploadProgress[vid.id]}%` }}
//                                 aria-valuenow={uploadProgress[vid.id]}
//                                 aria-valuemin="0"
//                                 aria-valuemax="100"
//                               >
//                                 {uploadProgress[vid.id]}%
//                               </div>
//                             </div>
//                           )}
//                           {!vid.isUploaded && (
//                             <button className="remove-btn" onClick={() => removeVideo(index)}>
//                               ×
//                             </button>
//                           )}
//                         </div>
//                       ))}
//                     </div>
//                   )}
//                   {errors.videos && <div className="text-danger">{errors.videos}</div>}
//                 </div>
//                 <p className="ah-text-sm gray-color-300 mb-0">
//                   Your promo video is a quick and compelling way for students to preview what they’ll learn in your course.
//                   Students considering your course are more likely to enroll if your promo video is well-made
//                 </p>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   }
// );

// export default StepTwo;